/* Language. Czech is primary and English is partial, on purpose.
   ------------------------------------------------------------------------
   CONTRACT — owned by the foundation. See PLAN.md before changing it.
   ------------------------------------------------------------------------

   Czech sits at the root (/), English under /en. The primary audience should
   not have to carry a locale segment for the language the material is written
   in, and a shared link should read as a Czech address. */

import { createContext, useContext } from "react";
import type { Locale, Localized } from "./types";

export const LOCALES: Locale[] = ["cs", "en"];
export const DEFAULT_LOCALE: Locale = "cs";

export interface Resolved {
  text: string;
  /* false when English was asked for and only Czech exists. The caller marks
     it; it never silently pretends to be a translation. */
  translated: boolean;
}

/* Resolve a localized value. A bare string is Czech — see Localized. */
export function resolve(value: Localized | undefined, locale: Locale): Resolved {
  if (value == null) return { text: "", translated: true };
  if (typeof value === "string") {
    return { text: value, translated: locale === "cs" };
  }
  if (locale === "en") {
    return value.en != null && value.en !== ""
      ? { text: value.en, translated: true }
      : { text: value.cs, translated: false };
  }
  return { text: value.cs, translated: true };
}

export interface I18n {
  locale: Locale;
  /* the text alone — the common case */
  t: (value: Localized | undefined) => string;
  /* text plus whether it is a real translation, for the untranslated mark */
  r: (value: Localized | undefined) => Resolved;
  /* a UI string by key, from content/locales/<locale>.json */
  ui: (key: string) => string;
  /* prefix a path with the locale: href("/s/zs2") -> "/en/s/zs2" */
  href: (path: string) => string;
}

export const I18nContext = createContext<I18n | null>(null);

export function useI18n(): I18n {
  const value = useContext(I18nContext);
  if (!value) throw new Error("useI18n outside <I18nProvider>");
  return value;
}

/* Build the context value. Kept out of the component so it can be unit-tested
   and so the provider stays a few lines. */
export function makeI18n(locale: Locale, strings: Record<string, string>): I18n {
  return {
    locale,
    t: (v) => resolve(v, locale).text,
    r: (v) => resolve(v, locale),
    /* A missing key shows the key. Silence would hide the hole until someone
       noticed a blank button in a workshop. */
    ui: (key) => strings[key] ?? key,
    href: (path) => (locale === DEFAULT_LOCALE ? path : `/${locale}${path}`),
  };
}

/* Which locale a pathname is asking for, and what is left of the path. */
export function splitLocale(pathname: string): { locale: Locale; rest: string } {
  const m = /^\/(cs|en)(\/.*)?$/.exec(pathname);
  if (!m) return { locale: DEFAULT_LOCALE, rest: pathname };
  return { locale: m[1] as Locale, rest: m[2] ?? "/" };
}
